import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

const RED = '#FF3B3B';

function useFluctuatingValue(active: boolean, min: number, max: number, interval = 1100) {
  const [val, setVal] = useState(min);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => {
      setVal(Math.round((min + Math.random() * (max - min)) * 10) / 10);
    }, interval);
    return () => clearInterval(id);
  }, [active, min, max, interval]);
  return val;
}

function AnimatedRedStat({ label, fallback, active }: { label: string; fallback: string; active: boolean }) {
  const ping = useFluctuatingValue(active, 120, 280, 900);
  const loss = useFluctuatingValue(active, 2, 30, 1200);
  const fps = useFluctuatingValue(active, 25, 60, 1400);

  switch (label) {
    case 'Average Ping':
      return (
        <motion.span key={ping} initial={{ opacity: 0.6 }} animate={{ opacity: 1 }}>
          {Math.round(ping)}ms
        </motion.span>
      );
    case 'Packet Loss':
      return (
        <motion.span key={loss} initial={{ opacity: 0.6 }} animate={{ opacity: 1 }} style={{ color: loss > 15 ? '#FF3B3B' : '#FF6B6B' }}>
          {loss.toFixed(1)}%
        </motion.span>
      );
    case 'Rubberbanding':
      return <span>{ping > 200 ? 'SEVERE' : 'Constant'}</span>;
    case 'FPS Drops':
      return (
        <motion.span key={fps} initial={{ opacity: 0.6 }} animate={{ opacity: 1 }}>
          -{Math.round(fps)}%
        </motion.span>
      );
    case 'Disconnections':
      return <span>{loss > 20 ? 'CRITICAL' : 'Frequent'}</span>;
    case 'Emotional State':
      return <span>{ping > 220 ? 'UNINSTALLING' : 'RAGE'}</span>;
    default:
      return <span>{fallback}</span>;
  }
}

export function RedPillCard() {
  const { t } = useTranslation(['components/trial/lp/v2c']);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  const ping = useFluctuatingValue(inView, 120, 280, 900);
  const loss = useFluctuatingValue(inView, 2, 30, 1200);
  const jitter = useFluctuatingValue(inView, 20, 80, 1000);

  useEffect(() => {
    if (!inView) return undefined;
    const id = setInterval(() => {}, 1000);
    return () => clearInterval(id);
  }, [inView]);

  return (
    <div ref={ref} className='relative h-full overflow-hidden rounded-xl border p-6 md:me-4 md:p-8' style={{ backgroundColor: `${RED}06`, borderColor: `${RED}30` }}>
      <div className='absolute inset-x-0 top-0 h-1' style={{ background: `linear-gradient(90deg, transparent, ${RED}, transparent)` }} />

      <div className='mb-6 font-mono text-xs uppercase tracking-wider' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: RED }}>
        {t('red_pill.header')}
      </div>

      <h3 className='mb-6 text-xl font-bold md:text-2xl' style={{ fontFamily: 'var(--font-bai)', color: RED }}>
        {t('red_pill.title')}
      </h3>

      <div className='space-y-4'>
        {['Average Ping', 'Packet Loss', 'Rubberbanding', 'FPS Drops', 'Disconnections', 'Emotional State'].map((label, i) => (
          <div key={i} className='flex items-center justify-between border-b py-2 font-mono text-sm' style={{ fontFamily: 'var(--font-jetbrains), monospace', borderColor: `${RED}15` }}>
            <span style={{ color: `${RED}90` }}>{t(`red_pill.stats.${i}.label`)}</span>
            <span className='font-bold tabular-nums' style={{ color: RED }}>
              <AnimatedRedStat label={label} fallback={t(`red_pill.stats.${i}.value`)} active={inView} />
            </span>
          </div>
        ))}
      </div>

      <div className='mt-4 rounded-lg border p-3 md:p-4' style={{ backgroundColor: `${RED}08`, borderColor: `${RED}25` }}>
        <div className='py-0.5 font-mono text-[11px] leading-relaxed md:text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: RED }}>
          {'>'} PING:{' '}
          <motion.span key={Math.round(ping)} initial={{ opacity: 0.5 }} animate={{ opacity: 1 }}>
            {Math.round(ping)}ms
          </motion.span>{' '}
          [CRITICAL]
        </div>
        <div className='py-0.5 font-mono text-[11px] leading-relaxed md:text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: RED }}>
          {'>'} LOSS:{' '}
          <motion.span key={loss} initial={{ opacity: 0.5 }} animate={{ opacity: 1 }}>
            {loss.toFixed(1)}%
          </motion.span>{' '}
          [{loss > 15 ? 'CRITICAL' : 'DEGRADED'}]
        </div>
        <div className='py-0.5 font-mono text-[11px] leading-relaxed md:text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: RED }}>
          {'>'} JITTER:{' '}
          <motion.span key={Math.round(jitter)} initial={{ opacity: 0.5 }} animate={{ opacity: 1 }}>
            {Math.round(jitter)}ms
          </motion.span>{' '}
          [UNSTABLE]
        </div>
      </div>

      <div className='mt-4 text-center font-mono text-xs' style={{ fontFamily: 'var(--font-jetbrains), monospace', color: `${RED}60` }}>
        <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
          {'>'} STATUS: ENSLAVED_BY_ISP_ROUTING
        </motion.span>
      </div>
    </div>
  );
}
